import { ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, inject, input, viewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslocoModule } from '@jsverse/transloco';
import { GuidelineService, UserService } from 'shared/domain';
import { BannerComponent, NoResultsComponent } from 'shared/ui-common';
import { TreetopPropertiesProvider } from 'shared/util-common';

import { AuthService } from '@treetop/authentication';
import { WorkflowWidget } from '@treetop/shared/model';

const WORKFLOWS_EMBED_READY_MESSAGE_TYPE = 'TT_WORKFLOWS_EMBED_READY';
const GUIDELINES_EMBED_AUTH_MESSAGE_TYPE = 'TT_GUIDELINES_EMBED_AUTH';

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tt-workflow-widget',
  templateUrl: './workflow-widget.component.html',
  imports: [BannerComponent, NoResultsComponent, TranslocoModule],
  styleUrls: ['./workflow-widget.component.scss'],
})
export class WorkflowWidgetComponent {
  private destroyRef = inject(DestroyRef);
  private sanitizer = inject(DomSanitizer);
  private guidelineService = inject(GuidelineService);
  private authService = inject(AuthService);
  private workflowFrame = viewChild<ElementRef<HTMLIFrameElement>>('workflowFrame');
  private userService = inject(UserService);

  protected widget = input.required<WorkflowWidget>();

  protected hasUpdate = computed(() => {
    const widget = this.widget();
    return widget.isOutdated && widget.latestVersion ? true : false;
  });

  protected missingEmbedReason = computed(() => {
    const workflowId = this.widget().workflowId;
    const workflowVersion = this.widget().workflowVersion;
    const workflowFrontendBaseUrl = this.workflowFrontendBaseUrl();
    const coreOrganisationId =
      this.guidelineService.guidelineData()?.organisation?.coreOrganizationId ??
      this.userService.user()?.currentOrganisation?.coreOrganizationId;

    if (!workflowId) return 'Missing workflow id.';
    if (!workflowVersion) return 'Missing workflow version.';
    if (!coreOrganisationId) return 'Missing organisation id.';
    if (!workflowFrontendBaseUrl) return 'Missing workflow frontend base url.';
    return null;
  });

  protected workflowUrlString = computed(() => {
    const workflowId = this.widget().workflowId;
    const workflowVersion = this.widget().workflowVersion;
    const workflowFrontendBaseUrl = this.workflowFrontendBaseUrl();
    const coreOrganisationId =
      this.guidelineService.guidelineData()?.organisation?.coreOrganizationId ??
      this.userService.user()?.currentOrganisation?.coreOrganizationId;
    const parentOrigin = window.location.origin;

    if (!workflowId || !workflowVersion || !coreOrganisationId || !workflowFrontendBaseUrl) {
      return null;
    }

    const embedPath = `/organizations/${coreOrganisationId}/workflows/${workflowId}/versions/${workflowVersion}/embed`;

    if (this.hasUpdate()) {
      return `${workflowFrontendBaseUrl}${embedPath}?maxZoom=1&parentOrigin=${encodeURIComponent(parentOrigin)}`;
    }

    return `${workflowFrontendBaseUrl}${embedPath}?mode=view&maxZoom=1&parentOrigin=${encodeURIComponent(parentOrigin)}`;
  });

  constructor() {
    const handler = (event: MessageEvent) => {
      const eventData = event.data as { type?: string } | undefined;
      const messageType = typeof event.data === 'string' ? event.data : eventData?.type;

      // The workflow app pings when it is ready to receive the token.
      if (messageType !== WORKFLOWS_EMBED_READY_MESSAGE_TYPE) return;

      const workflowUrl = this.workflowUrlString();
      const expectedOrigin = workflowUrl ? this.tryGetOrigin(workflowUrl) : null;
      const expectedWindow = this.workflowFrame()?.nativeElement.contentWindow;
      if (!expectedOrigin || !expectedWindow) return;

      if (event.origin !== expectedOrigin) return;
      if (event.source !== expectedWindow) return;

      this.postAccessToken(expectedWindow, expectedOrigin);
    };

    window.addEventListener('message', handler);
    this.destroyRef.onDestroy(() => window.removeEventListener('message', handler));
  }

  protected workflowUrl = computed(() => {
    const url = this.workflowUrlString();
    if (!url) {
      return null;
    }

    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });

  private postAccessToken(target: Window, targetOrigin: string) {
    const accessToken = this.authService.getAccessToken() ?? undefined;
    if (!accessToken) return;

    try {
      target.postMessage(
        {
          type: GUIDELINES_EMBED_AUTH_MESSAGE_TYPE,
          accessToken,
        },
        targetOrigin,
      );
    } catch {
      // no-op (e.g. cross-origin window restrictions)
    }
  }

  private tryGetOrigin(url: string): string | null {
    try {
      return new URL(url).origin;
    } catch {
      return null;
    }
  }

  private workflowFrontendBaseUrl(): string | null {
    const baseUrl = TreetopPropertiesProvider.properties.workflowFrontendBaseUrl.trim().replace(/\/+$/, '');

    try {
      const url = new URL(baseUrl);
      return url.protocol === 'http:' || url.protocol === 'https:' ? baseUrl : null;
    } catch {
      return null;
    }
  }
}
